/**
 * PublishEditorState — Selection and interaction state for the Publish Editor
 *
 * sl-9qm.3.3: Centralized state for component selection, hover, and drag
 * within the Publish Editor's canvas. Consumed by the shell and slot components.
 *
 * sl-pbux.7: Extended with preset/variant editor modal intents so the
 * inspector tabs can open the StylePreset/StyleVariant editors without
 * prop-drilling through Inspector → InspectorShell → tab. Mounted modals
 * subscribe to the intent LiveData and render when non-null.
 *
 * SOLID:
 * - S: Only manages editor interaction state (not layout, not data)
 */
import { LiveData, Service } from '@toeverything/infra';

export interface DragState {
  /** Component type being dragged (from palette) */
  componentType: string;
  /** Current mouse position */
  x: number;
  y: number;
}

/**
 * sl-pbux.7.1: Intent payload for the StylePreset-Editor modal.
 *   - `new`   → blank preset wizard (target picker, then field defaults)
 *   - `clone` → prefill from `sourceId` (default or custom), id becomes
 *               `${sourceId}-copy-${nanoid(6)}`
 *   - `edit`  → open existing custom preset by id; defaults are not editable
 */
export type PresetEditorIntent =
  | { readonly mode: 'new' }
  | { readonly mode: 'clone'; readonly sourceId: string }
  | { readonly mode: 'edit'; readonly presetId: string };

/**
 * sl-pbux.7.2: Intent payload for the StyleVariant-Editor modal.
 *   - `new`  → variant wizard scoped to the bound preset
 *   - `edit` → open existing variant by id
 */
export type VariantEditorIntent =
  | { readonly mode: 'new'; readonly presetId: string }
  | { readonly mode: 'edit'; readonly variantId: string };

/**
 * sl-bpu6.8: Editor-chrome mode — `edit` shows the DnD canvas with slot
 * outlines, palette and inspector wiring; `preview` renders the same
 * ViewConfig exactly as a reader would see it, disabling DnD and slot
 * affordances.
 */
export type EditorMode = 'edit' | 'preview';

/**
 * sl-bpu6.8: Responsive-test viewport. `desktop` leaves the canvas
 * full-width; `tablet`/`mobile` constrain the canvas width so authors
 * can check responsive behaviour without resizing the browser.
 */
export type ViewportSize = 'desktop' | 'tablet' | 'mobile';

/**
 * sl-sbux.7: Top-level editor mode switch. The custom-app editor
 * exposes five orthogonal authoring surfaces:
 *   - `wizard`  → guided 5-step custom app builder wizard (sl-sbux.1). Default mode.
 *   - `builder` → free-form drag-and-drop block canvas.
 *   - `design`  → LLM-friendly styling stack (sl-sbux.8).
 *   - `translations` → content translation drafting surface.
 *   - `permissions` → effective document/folder permissions for the app.
 *   - `repair`  → schema-validation surface for the standard custom app DBs.
 *
 * The URL-param `?mode=wizard|builder|design|translations|repair` is the source of truth;
 * the service mirrors it bidirectionally so deep links land directly without
 * a wizard-mount detour and switches inside the editor stay in sync with the
 * browser URL. The default `wizard` mode clears the param.
 */
export type EditorViewMode =
  | 'wizard'
  | 'builder'
  | 'content'
  | 'design'
  | 'translations'
  | 'access'
  | 'agent-tools'
  | 'repair';

/**
 * sl-sbux.7: Wizard step persistence. The wizard owns its step labels and
 * help copy, but the active step is held here so that mode switches
 * (Builder → Design → Builder) restore the same step without re-mounting
 * the wizard state.
 */
export type WizardStepId =
  | 'pages'
  | 'structure'
  | 'content'
  | 'preview'
  | 'publish';

/**
 * sl-sbux.7: Optional banner navigation request. When the wizard surfaces
 * a fail-loud banner ("open Repair view") the click writes an issue-id
 * into this LiveData; the RepairView consumes it to scroll/highlight the
 * referenced issue and resets the field once the user acknowledges.
 */
export interface RepairNavigationRequest {
  readonly issueId: string;
  readonly requestedAt: number;
}

export interface WizardSectionStatus {
  readonly done: boolean;
  readonly issueCount: number;
}

export type WizardSectionStatusMap = Readonly<
  Partial<Record<WizardStepId, WizardSectionStatus>>
>;

export class PublishEditorState extends Service {
  /** Currently selected component in the canvas */
  readonly selectedComponentId$ = new LiveData<string | null>(null);

  /**
   * Monotonic selection intent counter. Re-selecting the same component must
   * still reopen the inspector after a user has visited another sidebar tab;
   * `selectedComponentId$` alone de-duplicates equal values.
   */
  readonly selectionRequestVersion$ = new LiveData<number>(0);

  /** Currently hovered component in the canvas */
  readonly hoveredComponentId$ = new LiveData<string | null>(null);

  /** Active drag operation (from palette to canvas) */
  readonly dragState$ = new LiveData<DragState | null>(null);

  /** sl-bpu6.8: edit vs preview chrome */
  readonly editorMode$ = new LiveData<EditorMode>('edit');

  /** Route whose page-scoped components are shown in the Advanced Builder. */
  readonly activeBuilderPageId$ = new LiveData<string | null>(null);

  /** sl-bpu6.8: simulated viewport width for responsive test */
  readonly viewport$ = new LiveData<ViewportSize>('desktop');

  /**
   * sl-pbux.8.4: builder reduced-motion simulation. When `true`, the canvas
   * applies `reduced-motion` style variants unconditionally (and motion
   * presets honour the policy as if `prefers-reduced-motion: reduce` were
   * set). `false` leaves the system preference in charge.
   */
  readonly simulatedReducedMotion$ = new LiveData<boolean>(false);

  /** sl-sbux.7: top-level editor mode (wizard | builder | design | repair) */
  readonly editorViewMode$ = new LiveData<EditorViewMode>('wizard');

  /** sl-sbux.7: persisted wizard step so mode switches stay verlustfrei */
  readonly wizardStep$ = new LiveData<WizardStepId>('structure');

  /** sl-sbux.7: set of step ids the user reached at least once */
  readonly wizardReachedSteps$ = new LiveData<ReadonlySet<WizardStepId>>(
    new Set<WizardStepId>(['structure'])
  );

  /** sl-sbux.7: pending repair-view focus request (issue id + tick) */
  readonly repairNavigationRequest$ =
    new LiveData<RepairNavigationRequest | null>(null);

  /**
   * sl-sbux.7: live count of schema errors surfaced by Reparatur. Drives the
   * Reparatur-tab badge in the top-bar mode switch so users see open issues
   * without a wizard banner that interrupts the active step.
   */
  readonly schemaErrorCount$ = new LiveData<number>(0);

  /** Errors that block publishing, including database-health failures. */
  readonly blockingIssueCount$ = new LiveData<number>(0);

  /** Non-blocking structural warnings shown by review and navigation. */
  readonly warningIssueCount$ = new LiveData<number>(0);

  private structuralIssueCount = 0;

  private structuralWarningCount = 0;

  private databaseHealthIssueCount = 0;
  /** Compact setup completeness shown beside the persistent app navigation. */
  readonly wizardSectionStatus$ = new LiveData<WizardSectionStatusMap>({});

  /** sl-pbux.7.1: active intent for the StylePreset-Editor modal */
  readonly presetEditorIntent$ = new LiveData<PresetEditorIntent | null>(null);

  /** sl-pbux.7.2: active intent for the StyleVariant-Editor modal */
  readonly variantEditorIntent$ = new LiveData<VariantEditorIntent | null>(
    null
  );

  select(componentId: string | null): void {
    this.selectionRequestVersion$.next(this.selectionRequestVersion$.value + 1);
    this.selectedComponentId$.next(componentId);
  }

  hover(componentId: string | null): void {
    this.hoveredComponentId$.next(componentId);
  }

  startDrag(componentType: string, x: number, y: number): void {
    this.dragState$.next({ componentType, x, y });
  }

  updateDrag(x: number, y: number): void {
    const current = this.dragState$.value;
    if (!current) return;
    this.dragState$.next({ ...current, x, y });
  }

  endDrag(): void {
    this.dragState$.next(null);
  }

  setEditorMode(mode: EditorMode): void {
    this.editorMode$.next(mode);
    // Leaving edit mode clears selection — preview has no slot affordances.
    if (mode === 'preview') {
      this.selectedComponentId$.next(null);
      this.hoveredComponentId$.next(null);
    }
  }

  setActiveBuilderPage(pageId: string | null): void {
    if (this.activeBuilderPageId$.value === pageId) return;
    this.activeBuilderPageId$.next(pageId);
    this.select(null);
  }

  setViewport(viewport: ViewportSize): void {
    this.viewport$.next(viewport);
  }

  setSimulatedReducedMotion(enabled: boolean): void {
    this.simulatedReducedMotion$.next(enabled);
  }

  openPresetEditor(intent: PresetEditorIntent): void {
    this.presetEditorIntent$.next(intent);
  }

  closePresetEditor(): void {
    this.presetEditorIntent$.next(null);
  }

  openVariantEditor(intent: VariantEditorIntent): void {
    this.variantEditorIntent$.next(intent);
  }

  closeVariantEditor(): void {
    this.variantEditorIntent$.next(null);
  }

  setEditorViewMode(mode: EditorViewMode): void {
    if (this.editorViewMode$.value === mode) return;
    this.editorViewMode$.next(mode);
  }

  setWizardStep(step: WizardStepId): void {
    if (this.wizardStep$.value === step) return;
    this.wizardStep$.next(step);
    const reached = this.wizardReachedSteps$.value;
    if (reached.has(step)) return;
    const next = new Set<WizardStepId>(reached);
    next.add(step);
    this.wizardReachedSteps$.next(next);
  }

  requestRepairFocus(issueId: string): void {
    this.repairNavigationRequest$.next({
      issueId,
      requestedAt: Date.now(),
    });
  }

  clearRepairFocus(): void {
    this.repairNavigationRequest$.next(null);
  }

  setSchemaErrorCount(count: number): void {
    this.structuralIssueCount = Math.max(0, count);
    this.structuralWarningCount = 0;
    this.publishCombinedIssueCount();
  }

  setStructuralIssueCounts(errors: number, warnings: number): void {
    this.structuralIssueCount = Math.max(0, errors);
    this.structuralWarningCount = Math.max(0, warnings);
    this.publishCombinedIssueCount();
  }

  setDatabaseHealthIssueCount(count: number): void {
    this.databaseHealthIssueCount = Math.max(0, count);
    this.publishCombinedIssueCount();
  }

  resetIssueCounts(): void {
    this.structuralIssueCount = 0;
    this.structuralWarningCount = 0;
    this.databaseHealthIssueCount = 0;
    this.publishCombinedIssueCount();
  }

  private publishCombinedIssueCount(): void {
    const blocking = this.structuralIssueCount + this.databaseHealthIssueCount;
    const warnings = this.structuralWarningCount;
    const count = blocking + warnings;
    if (this.blockingIssueCount$.value !== blocking) {
      this.blockingIssueCount$.next(blocking);
    }
    if (this.warningIssueCount$.value !== warnings) {
      this.warningIssueCount$.next(warnings);
    }
    if (this.schemaErrorCount$.value === count) return;
    this.schemaErrorCount$.next(count);
  }

  setWizardSectionStatus(status: WizardSectionStatusMap): void {
    this.wizardSectionStatus$.next(status);
  }

  /** Reset all state (e.g., when leaving publish mode) */
  reset(): void {
    this.selectedComponentId$.next(null);
    this.selectionRequestVersion$.next(0);
    this.hoveredComponentId$.next(null);
    this.dragState$.next(null);
    this.editorMode$.next('edit');
    this.activeBuilderPageId$.next(null);
    this.viewport$.next('desktop');
    this.simulatedReducedMotion$.next(false);
    this.presetEditorIntent$.next(null);
    this.variantEditorIntent$.next(null);
    this.editorViewMode$.next('wizard');
    this.wizardStep$.next('structure');
    this.wizardReachedSteps$.next(new Set<WizardStepId>(['structure']));
    this.repairNavigationRequest$.next(null);
    this.resetIssueCounts();
    this.wizardSectionStatus$.next({});
  }
}
